import { Alert, Button, Col, PageHeader, Row } from 'antd';
import PermissionsWrapper from 'app/components/PermissionsWrapper/PermissionsWrapper';
import {
  selectSelectedCompanyGroupId,
  selectSelectedCompanyId,
} from 'app/containers/Company/slice/selectors';
import { selectSelectedEnvId } from 'app/containers/HiikoEnv/slice/selectors';
import MapCard from 'app/containers/Map/parts/MapCard/MapCard';
import { useMapSlice } from 'app/containers/Map/slice';
import {
  selectMapLoading,
  selectSingleFieldOnMap,
} from 'app/containers/Map/slice/selectors';
import { MapModeEnum } from 'app/containers/Map/slice/types';
import { selectSelectedSeasonId } from 'app/containers/Season/slice/selectors';
import { PermissionsEnum } from 'app/containers/User/slice/types';
import { RouteConstants } from 'app/routes';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { generatePath, RouteComponentProps } from 'react-router-dom';
import { MenuInfo } from 'types';
import { LEFT_COL_WIDTH } from 'utils/constants';
import { useResponsiveQuery } from 'utils/responsiveQuery/useResponsiveQuery';
import FieldInfo from '../../parts/FieldInfo/FieldInfo';
import FieldSubmenu from '../../parts/FieldSubmenu/FieldSubmenu';
import FieldTasks from '../../parts/FieldTasks/FieldTasks';
import { fieldSubmenuEnum } from '../../slice/types';

import * as S from './FieldDetails.styles';

interface IReactRouterParams {
  fieldId: string;
}

interface IProps extends RouteComponentProps<IReactRouterParams> {}

const TAB_PARAM = 'tab';

const FieldDetails = ({
  history,
  location,
  match: {
    params: { fieldId },
  },
}: IProps) => {
  const dispatch = useDispatch();
  const { mobileOrTablet } = useResponsiveQuery();
  const { t } = useTranslation();

  const { actions: mapActions } = useMapSlice();

  const envId = useSelector(selectSelectedEnvId);
  const seasonId = useSelector(selectSelectedSeasonId);
  const companyId = useSelector(selectSelectedCompanyId);
  const companyGroupId = useSelector(selectSelectedCompanyGroupId);
  const mappedField = useSelector(selectSingleFieldOnMap);
  const mapLoading = useSelector(selectMapLoading);

  const [selctedSubmenuKey, setSelctedSubmenuKey] = useState(
    fieldSubmenuEnum.map,
  );

  useEffect(() => {
    if (envId && seasonId) {
      dispatch(mapActions.setSingleFieldOnMapFieldId(Number(fieldId)));
      dispatch(
        mapActions.onLoad({
          companyId,
          seasonId,
          envId,
          companyGroupId,
        }),
      );
    }
  }, [
    mapActions,
    dispatch,
    fieldId,
    companyId,
    seasonId,
    envId,
    companyGroupId,
  ]);

  useEffect(() => {
    const query = new URLSearchParams(location.search);

    const filterValue = query.get(TAB_PARAM) as fieldSubmenuEnum | null;

    if (filterValue) {
      if (Object.values(fieldSubmenuEnum).includes(filterValue)) {
        const val = filterValue.toLowerCase() as fieldSubmenuEnum;
        setSelctedSubmenuKey(val);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const query = new URLSearchParams(location.search);

    const filterValueInParams = query.get(TAB_PARAM) || undefined;

    if (selctedSubmenuKey && selctedSubmenuKey !== filterValueInParams) {
      query.set(TAB_PARAM, selctedSubmenuKey);
    } else if (!selctedSubmenuKey && filterValueInParams) {
      query.delete(TAB_PARAM);
    }
    history.push({ search: query.toString() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selctedSubmenuKey, location.search]);

  const onClickMenu = (clickedItem: MenuInfo) => {
    const key = clickedItem.key as fieldSubmenuEnum;
    setSelctedSubmenuKey(key);
  };

  if (!envId || mapLoading) {
    return null;
  }

  if (!mappedField) {
    return (
      <Alert
        message={t(
          'Field.Details.FieldNotFound',
          'Field by selected season, environment and company not found.',
        )}
        type="info"
      />
    );
  }

  const responsivePadding = mobileOrTablet ? 6 : 12;

  return (
    <>
      <PageHeader
        onBack={() =>
          history.push(
            generatePath(RouteConstants.field.list, {
              envId,
            }),
          )
        }
        className="page-header"
        title={mappedField.fieldParameters.name}
      />

      <Row
        wrap={mobileOrTablet}
        gutter={responsivePadding}
        style={{
          paddingLeft: responsivePadding,
          paddingTop: responsivePadding,
          width: '100%',
        }}
      >
        <Col
          style={{
            paddingBottom: 6,
            minWidth: mobileOrTablet ? '100%' : LEFT_COL_WIDTH,
          }}
        >
          <FieldSubmenu
            onClickMenu={onClickMenu}
            selectedKey={selctedSubmenuKey}
          />
        </Col>
        <S.MapAndResourcesCol flex="auto">
          {selctedSubmenuKey === fieldSubmenuEnum.map ? (
            <MapCard
              mode={MapModeEnum.singleField}
              extraHeaderButtons={[
                <PermissionsWrapper
                  requiredEditPermissions={[
                    PermissionsEnum.fieldAdministration,
                  ]}
                >
                  <Button
                    onClick={() =>
                      history.push(
                        generatePath(RouteConstants.field.editField, {
                          envId,
                          fieldId: mappedField.fieldParameters.fieldId,
                        }),
                      )
                    }
                  >
                    {t('Map.EditField', 'Edit field')}
                  </Button>
                </PermissionsWrapper>,
              ]}
            />
          ) : selctedSubmenuKey === fieldSubmenuEnum.information ? (
            <FieldInfo mappedField={mappedField} />
          ) : selctedSubmenuKey === fieldSubmenuEnum.tasks ? (
            <FieldTasks envId={envId} fieldId={Number(fieldId)} />
          ) : null}
        </S.MapAndResourcesCol>
      </Row>
    </>
  );
};

export default FieldDetails;
